/**
 * Investor Update Template Form
 * Investor communication and metrics
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const InvestorUpdateForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '', period: '', date: new Date().toISOString().split('T')[0], prepared_by: '',
    executive_summary: '', key_highlights: [''],
    revenue: { current: '', previous: '', growth: '', notes: '' },
    arr_mrr: { arr: '', mrr: '', growth: '', notes: '' },
    burn_rate: { monthly: '', runway: '', notes: '' },
    customer_metrics: { total: '', new: '', churned: '', nps: '' },
    product_updates: [''], roadmap_progress: [{ milestone: '', status: '', target_date: '' }],
    team_updates: [''], key_hires: [''],
    market_updates: [''], competitive_landscape: '',
    asks_from_investors: [''],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    period: 'January 2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'CEO',
    executive_summary: 'Strong start to 2025 with 18% MoM revenue growth and successful launch of AI features. Customer acquisition accelerated with 3 enterprise deals closed. Team expanded to 120 employees. Well-positioned for Series B in Q3.',
    key_highlights: [
      '🚀 Revenue up 18% MoM to $4.2M MRR',
      '🤝 3 new enterprise customers (combined $800K ARR)',
      '🔧 AI-powered search launched in beta - 90% positive feedback',
      '👥 Headcount grew to 120 (net +8)',
      '💰 18 months runway at current burn rate'
    ],
    revenue: { current: '$4.2M', previous: '$3.6M', growth: '+18%', notes: 'Strong enterprise pipeline for Q1' },
    arr_mrr: { arr: '$50M', mrr: '$4.2M', growth: '+22% YoY', notes: 'Exceeding plan by 8%' },
    burn_rate: { monthly: '$450K', runway: '18 months', notes: 'Optimized spend, runway extended' },
    customer_metrics: { total: '2,650', new: '180', churned: '45', nps: '65' },
    product_updates: [
      'Platform V2 at 85% completion - Q2 launch on track',
      'AI-powered search beta launched - 500 beta users, 90% satisfaction',
      'Mobile app v2 in development - redesigned UX',
      'Enterprise SSO feature completed'
    ],
    roadmap_progress: [
      { milestone: 'Platform V2 Launch', status: 'On Track', target_date: '2025-04-01' },
      { milestone: 'AI Features GA', status: 'On Track', target_date: '2025-03-15' },
      { milestone: 'Mobile App v2', status: 'At Risk', target_date: '2025-05-01' },
      { milestone: 'APAC Expansion', status: 'On Track', target_date: '2025-06-01' }
    ],
    team_updates: [
      'Engineering team now at 48 (grew by 8 in January)',
      'Opened Singapore office - first APAC presence',
      'Promoted 3 team leads to director roles',
      'Employee satisfaction score: 4.2/5'
    ],
    key_hires: [
      'VP of Sales - Jane Smith (ex-Salesforce)',
      'Head of APAC - Michael Lee (ex-Stripe)',
      'Senior Engineering Manager - Alex Kumar (ex-Google)'
    ],
    market_updates: [
      'Industry growing at 25% CAGR - we\'re outpacing at 40%',
      'Competitor XYZ raised $50M - focusing on SMB segment',
      'New partnership discussions with major cloud provider',
      'Government contract RFP submitted - $2M potential'
    ],
    competitive_landscape: 'We maintain strong differentiation through our AI capabilities and enterprise focus. Main competitor XYZ is focusing on SMB after their funding, creating opportunity in enterprise segment. Our platform V2 will further widen the gap.',
    asks_from_investors: [
      '🤝 Introductions to enterprise CIOs in financial services',
      '📞 Reference calls for Series B process (Q3)',
      '🎯 Feedback on APAC market entry strategy'
    ],
    rai_assignments: [
      { task: 'Prepare Series B materials', responsible: 'CFO', accountable: 'CEO', informed: 'Board', due_date: '2025-06-01', status: 'Pending' }
    ],
    followups: [
      { item: 'Schedule investor 1:1 calls', owner: 'CEO', due_date: '2025-02-15', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Investor Update Draft";
      const payload = {
        name: draftName,
        template_type: "Investor Update",
        template_id: "investor-update",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "investor-update", "Investor Update", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("investor-update", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/investor-update`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Investor_Update_${formData.company_name.replace(/\s+/g, '_') || 'Company'}_${formData.period.replace(/\s+/g, '_')}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Investor Update",
        formData,
        settings: activeSettings || {},
        templateType: "Investor Update",
        templateId: "investor-update",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const renderListSection = (field, label, placeholder, bgColor = '') => (
    <div className={`${bgColor ? `${bgColor} p-3 rounded-lg` : ''}`}>
      <Label className="font-semibold">{label}</Label>
      {formData[field].map((item, idx) => (
        <div key={idx} className="flex gap-2 mt-2">
          <Input value={item} onChange={(e) => {
            const items = [...formData[field]];
            items[idx] = e.target.value;
            setFormData({ ...formData, [field]: items });
          }} placeholder={placeholder} className="text-sm" />
          <Button variant="ghost" size="icon" onClick={() => setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== idx) })}><Trash2 className="h-4 w-4 text-red-500" /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, [field]: [...formData[field], ''] })} className="mt-2"><Plus className="h-4 w-4 mr-1" /> Add</Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Investor Update</h1><p className="text-sm text-gray-500">Investor communication and metrics</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Update Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>Company Name *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="TechCorp Inc." /></div>
              <div><Label>Period</Label><Input value={formData.period} onChange={(e) => setFormData({...formData, period: e.target.value})} placeholder="January 2025" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <div><Label>Executive Summary</Label><Textarea value={formData.executive_summary} onChange={(e) => setFormData({...formData, executive_summary: e.target.value})} placeholder="High-level summary for investors..." rows={4} /></div>

            {renderListSection('key_highlights', 'Key Highlights', 'Highlight', 'bg-blue-50')}

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <Label className="font-semibold text-green-800">Revenue</Label>
                <div className="space-y-2 mt-2">
                  <div><Label className="text-xs">Current</Label><Input value={formData.revenue.current} onChange={(e) => setFormData({...formData, revenue: {...formData.revenue, current: e.target.value}})} placeholder="$4.2M" className="text-sm" /></div>
                  <div><Label className="text-xs">Previous</Label><Input value={formData.revenue.previous} onChange={(e) => setFormData({...formData, revenue: {...formData.revenue, previous: e.target.value}})} placeholder="$3.6M" className="text-sm" /></div>
                  <div><Label className="text-xs">Growth</Label><Input value={formData.revenue.growth} onChange={(e) => setFormData({...formData, revenue: {...formData.revenue, growth: e.target.value}})} placeholder="+18%" className="text-sm" /></div>
                </div>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg">
                <Label className="font-semibold text-purple-800">ARR / MRR</Label>
                <div className="space-y-2 mt-2">
                  <div><Label className="text-xs">ARR</Label><Input value={formData.arr_mrr.arr} onChange={(e) => setFormData({...formData, arr_mrr: {...formData.arr_mrr, arr: e.target.value}})} placeholder="$50M" className="text-sm" /></div>
                  <div><Label className="text-xs">MRR</Label><Input value={formData.arr_mrr.mrr} onChange={(e) => setFormData({...formData, arr_mrr: {...formData.arr_mrr, mrr: e.target.value}})} placeholder="$4.2M" className="text-sm" /></div>
                  <div><Label className="text-xs">Growth</Label><Input value={formData.arr_mrr.growth} onChange={(e) => setFormData({...formData, arr_mrr: {...formData.arr_mrr, growth: e.target.value}})} placeholder="+22% YoY" className="text-sm" /></div>
                </div>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg">
                <Label className="font-semibold text-orange-800">Burn & Runway</Label>
                <div className="space-y-2 mt-2">
                  <div><Label className="text-xs">Monthly Burn</Label><Input value={formData.burn_rate.monthly} onChange={(e) => setFormData({...formData, burn_rate: {...formData.burn_rate, monthly: e.target.value}})} placeholder="$450K" className="text-sm" /></div>
                  <div><Label className="text-xs">Runway</Label><Input value={formData.burn_rate.runway} onChange={(e) => setFormData({...formData, burn_rate: {...formData.burn_rate, runway: e.target.value}})} placeholder="18 months" className="text-sm" /></div>
                  <div><Label className="text-xs">Notes</Label><Input value={formData.burn_rate.notes} onChange={(e) => setFormData({...formData, burn_rate: {...formData.burn_rate, notes: e.target.value}})} placeholder="Notes" className="text-sm" /></div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-cyan-50 rounded-lg">
              <Label className="font-semibold text-cyan-800">Customer Metrics</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                <div><Label className="text-xs">Total Customers</Label><Input value={formData.customer_metrics.total} onChange={(e) => setFormData({...formData, customer_metrics: {...formData.customer_metrics, total: e.target.value}})} placeholder="2,650" className="text-sm" /></div>
                <div><Label className="text-xs">New This Period</Label><Input value={formData.customer_metrics.new} onChange={(e) => setFormData({...formData, customer_metrics: {...formData.customer_metrics, new: e.target.value}})} placeholder="180" className="text-sm" /></div>
                <div><Label className="text-xs">Churned</Label><Input value={formData.customer_metrics.churned} onChange={(e) => setFormData({...formData, customer_metrics: {...formData.customer_metrics, churned: e.target.value}})} placeholder="45" className="text-sm" /></div>
                <div><Label className="text-xs">NPS Score</Label><Input value={formData.customer_metrics.nps} onChange={(e) => setFormData({...formData, customer_metrics: {...formData.customer_metrics, nps: e.target.value}})} placeholder="65" className="text-sm" /></div>
              </div>
            </div>

            <Separator />

            {renderListSection('product_updates', 'Product Updates', 'Product update')}
            {renderListSection('team_updates', 'Team Updates', 'Team update')}
            {renderListSection('key_hires', 'Key Hires', 'Name - Title (ex-Company)')}
            {renderListSection('market_updates', 'Market Updates', 'Market insight')}

            <div><Label>Competitive Landscape</Label><Textarea value={formData.competitive_landscape} onChange={(e) => setFormData({...formData, competitive_landscape: e.target.value})} placeholder="Competitive analysis and positioning..." rows={3} /></div>

            {renderListSection('asks_from_investors', 'Asks from Investors', 'Request', 'bg-yellow-50')}

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default InvestorUpdateForm;
